/* AA Holiday live usability hotfix — 2026-10-13. */
(() => {
  class AAHolidayHero extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.slides = [...this.querySelectorAll('[role="tabpanel"]')];
      this.tabs = [...this.querySelectorAll('[role="tab"]')];
      this.selected = 0;
      if (!this.slides.length || this.slides.length !== this.tabs.length) return;
      this.abort = new AbortController();
      const options = { signal: this.abort.signal };
      this.hovered = this.matches(':hover');
      this.focused = false;
      this.inView = true;
      this.userPaused = false;
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.labels = {
        en: ['Previous slide', 'Next slide', 'Pause slideshow', 'Play slideshow'],
        de: ['Vorherige Folie', 'Nächste Folie', 'Diashow pausieren', 'Diashow starten'],
        fr: ['Diapositive précédente', 'Diapositive suivante', 'Mettre le diaporama en pause', 'Lire le diaporama'],
        es: ['Diapositiva anterior', 'Diapositiva siguiente', 'Pausar presentación', 'Reproducir presentación']
      }[document.documentElement.lang.split('-')[0]] || ['Previous slide', 'Next slide', 'Pause slideshow', 'Play slideshow'];
      this.controls = document.createElement('div');
      this.controls.className = 'aahgd__controls';
      [-1, 1].forEach((direction, index) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'aahgd__arrow aahgd__arrow--' + (direction < 0 ? 'previous' : 'next');
        button.setAttribute('aria-label', this.labels[index]);
        button.innerHTML = '<svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true" focusable="false"><path d="' + (direction < 0 ? 'M15 5l-7 7 7 7' : 'M9 5l7 7-7 7') + '" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>';
        button.addEventListener('click', () => this.manualSelect(this.selected + direction), options);
        this.controls.append(button);
      });
      this.rotationButton = document.createElement('button');
      this.rotationButton.type = 'button';
      this.rotationButton.className = 'aahgd__rotation';
      this.rotationButton.addEventListener('click', () => {
        this.userPaused = !this.userPaused;
        this.updateRotationButton();
        this.armRotation();
      }, options);
      this.append(this.controls, this.rotationButton);
      this.updateRotationButton();
      this.tabs.forEach((tab, index) => tab.addEventListener('click', () => this.manualSelect(index), options));
      this.querySelector('[role="tablist"]').addEventListener('keydown', event => {
        if (event.ctrlKey || event.metaKey || event.altKey) return;
        let target;
        if (event.key === 'ArrowRight') target = this.selected + 1;
        if (event.key === 'ArrowLeft') target = this.selected - 1;
        if (event.key === 'Home') target = 0;
        if (event.key === 'End') target = this.slides.length - 1;
        if (target !== undefined) {
          event.preventDefault();
          this.focused = true;
          this.manualSelect(target, true);
        }
      }, options);
      this.addEventListener('pointerenter', event => {
        if (event.pointerType === 'mouse' || event.pointerType === 'pen') {
          this.hovered = true;
          this.armRotation();
        }
      }, options);
      this.addEventListener('pointerleave', () => {
        this.hovered = false;
        this.armRotation();
      }, options);
      this.addEventListener('focusin', event => {
        if (event.target.matches(':focus-visible')) this.focused = true;
        this.armRotation();
      }, options);
      this.addEventListener('focusout', () => {
        queueMicrotask(() => {
          this.focused = this.contains(document.activeElement) && document.activeElement.matches(':focus-visible');
          this.armRotation();
        });
      }, options);
      this.addEventListener('pointerdown', event => {
        this.lastInteraction = Date.now();
        this.armRotation();
        if (event.pointerType === 'touch') this.touchStart = { x: event.clientX, y: event.clientY };
      }, options);
      this.addEventListener('pointerup', event => {
        const point = this.touchStart;
        this.touchStart = null;
        if (event.pointerType !== 'touch' || !point) return;
        const dx = event.clientX - point.x, dy = event.clientY - point.y;
        if (Math.abs(dx) > 60 && Math.abs(dx) > Math.abs(dy)) this.manualSelect(this.selected + (dx < 0 ? 1 : -1));
      }, options);
      this.addEventListener('pointercancel', () => { this.touchStart = null; }, options);
      document.addEventListener('visibilitychange', () => this.armRotation(), options);
      this.motion.addEventListener('change', () => {
        this.cancelTransition();
        this.updateRotationButton();
        this.armRotation();
      }, options);
      this.observer = new IntersectionObserver(entries => {
        this.inView = entries[0].isIntersecting && entries[0].intersectionRatio >= 0.15;
        this.armRotation();
      }, { threshold: [0, 0.15] });
      this.observer.observe(this);
      document.addEventListener('shopify:block:select', event => {
        const index = this.slides.findIndex(slide => slide.contains(event.target));
        if (index >= 0) {
          this.userPaused = true;
          this.updateRotationButton();
          this.manualSelect(index);
        }
      }, options);
      this.show(0);
      this.refreshPrice();
      this.armRotation();
    }
    disconnectedCallback() {
      clearTimeout(this.rotationTimer);
      this.cancelTransition();
      this.observer?.disconnect();
      this.abort?.abort();
      this.controls?.remove();
      this.rotationButton?.remove();
      this.ready = false;
    }
    updateRotationButton() {
      this.rotationButton.setAttribute('aria-label', this.labels[this.userPaused ? 3 : 2]);
      this.rotationButton.setAttribute('aria-pressed', String(this.userPaused));
      this.rotationButton.textContent = this.userPaused ? '▶' : 'Ⅱ';
      this.rotationButton.hidden = this.motion.matches;
    }
    armRotation() {
      clearTimeout(this.rotationTimer);
      if (!this.isConnected || this.slides.length < 2 || this.hovered || this.focused || this.userPaused || this.motion.matches || document.hidden || !this.inView) return;
      const interactionDelay = Math.max(0, 12000 - (Date.now() - (this.lastInteraction || 0)));
      this.rotationTimer = setTimeout(() => {
        this.show(this.selected + 1);
        this.armRotation();
      }, Math.max(5000, interactionDelay));
    }
    manualSelect(index, focus = false) {
      this.lastInteraction = Date.now();
      this.show(index, focus);
      this.armRotation();
    }
    cancelTransition() {
      this.transitionAnimations?.forEach(animation => animation.cancel());
      this.transitionAnimations = [];
      this.slides?.forEach((slide, i) => {
        slide.classList.remove('aahgd__slide--leaving');
        slide.style.removeProperty('top');
        slide.style.removeProperty('height');
        slide.removeAttribute('aria-hidden');
        slide.inert = false;
        slide.hidden = i !== this.selected;
      });
    }
    show(index, focus = false) {
      const previous = this.selected;
      const next = (index + this.slides.length) % this.slides.length;
      const changing = this.hasShown && next !== previous;
      this.cancelTransition();
      const oldSlide = this.slides[previous];
      const oldHeight = oldSlide.getBoundingClientRect().height;
      this.selected = next;
      this.slides.forEach((slide, i) => { slide.hidden = i !== next; });
      this.tabs.forEach((tab, i) => {
        tab.setAttribute('aria-selected', String(i === next));
        tab.tabIndex = i === next ? 0 : -1;
      });
      this.hasShown = true;
      if (changing && !this.motion.matches && typeof oldSlide.animate === 'function') {
        const incoming = this.slides[next];
        const direction = index < previous ? -1 : 1;
        const easing = 'cubic-bezier(.22,1,.36,1)';
        const animate = (element, frames, duration, delay = 0) => {
          if (!element) return;
          const animation = element.animate(frames, { duration, delay, easing, fill: 'backwards' });
          this.transitionAnimations.push(animation);
          return animation;
        };
        oldSlide.hidden = false;
        oldSlide.inert = true;
        oldSlide.setAttribute('aria-hidden', 'true');
        oldSlide.classList.add('aahgd__slide--leaving');
        oldSlide.style.top = this.querySelector('[role="tablist"]').getBoundingClientRect().height + 'px';
        oldSlide.style.height = oldHeight + 'px';
        const exit = animate(oldSlide, [{ opacity: 1 }, { opacity: 0 }], 650);
        exit.onfinish = () => {
          if (this.selected !== previous) {
            oldSlide.hidden = true;
            oldSlide.classList.remove('aahgd__slide--leaving');
            oldSlide.style.removeProperty('top');
            oldSlide.style.removeProperty('height');
            oldSlide.removeAttribute('aria-hidden');
            oldSlide.inert = false;
          }
        };
        animate(incoming, [{ opacity: 0 }, { opacity: 1 }], 800);
        animate(incoming.querySelector('.aahgd__scene img'), [
          { transform: 'scale(1.065) translateX(' + (direction * 1.6) + '%)' },
          { transform: 'scale(1) translateX(0)' }
        ], 1700);
        incoming.querySelectorAll('.aahgd__copy > *').forEach((element, i) => {
          animate(element, [
            { opacity: 0, transform: 'translateY(22px)' },
            { opacity: 1, transform: 'translateY(0)' }
          ], 850, 130 + i * 65);
        });
        incoming.querySelectorAll('.aahgd__proof > *').forEach((element, i) => {
          animate(element, [
            { opacity: 0, transform: 'translate3d(' + (direction * 22) + 'px,12px,0)' },
            { opacity: 1, transform: 'translate3d(0,0,0)' }
          ], 1050, 220 + i * 90);
        });
      }
      if (focus) this.tabs[next].focus();
    }
    async refreshPrice() {
      try {
        const [helper, response] = await Promise.all([import(this.dataset.currencyHelper), fetch(this.dataset.offerRules, { cache: 'no-cache' })]);
        if (!response.ok || !this.isConnected) return;
        const rules = await response.json();
        const context = helper.currencyContext(rules, this.dataset.currency);
        const total = context.offerMoney(context.offerBundleCents);
        document.querySelectorAll('.aahgd [data-offer-headline], .aahgd-picks [data-offer-headline]').forEach(element => {
          element.textContent = element.dataset.template.replaceAll('[[total]]', total);
        });
      } catch { /* Preserve the market-correct Liquid fallback. */ }
    }
  }
  if (!customElements.get('aa-holiday-hero')) customElements.define('aa-holiday-hero', AAHolidayHero);
})();
